/* tslint:disable */
/* eslint-disable */
/**
 * Business Orchestration Layer (BOL) API
 * Business Orchestration layer (BOL) API. Public part, exposed to BOL client applications.  Glossary | Definition -- | -- <strong>Logistics Object</strong> | A resource in the BOL system that represents a business entity, such as a QuotationRequest, Quotation, or Service. Logistics Objects are managed through the BOL API and can be accessed or modified by Users based on their permissions i.e. based on their AD Entra ID roles and ACL (Access Control List) modes. <strong>Member</strong> | A data owner in the BOL system, uniquely identified by its `memberIdentifier`. Members are used to manage access control and ownership of Logistics Objects. <strong>User</strong> | A person or entity that interacts with the BOL system, typically through a client application, and beeing authenticated. Users are associated with Members and can perform actions on Logistics Objects based on their access level. User access token includes the Member identifier as part of roles claims. Each path in the API is protected by a custom role defined in AD Entra ID, and operations on Logistics Objects are protected by specific Access modes defined by members. <strong>Access Control</strong> | The mechanism by which the BOL system restricts access to API paths and resources based on the identity of the User and their associated Member. Access control is enforced through AD Entra ID roles and ACL (Access Control List) modes defined for every single Logistics Object. <strong>ACL (Access Control List)</strong> | A set of authorizations that define what actions a User can perform on a Logistics Object. The level of access granted to Users is determined by ACL modes defined in authorizations for specific Logistics Objects. ACL Modes are not directly associated with Users but rather with Members, which are the data owners of Logistics Objects. Each Logistics Object has an ACL that specifies the access modes for different Members. Members need to exist in the BOL system to be granted access to Logistics Objects. <strong>ACL Mode</strong> | The specific level of access control applied to a Logistics Object, determining what actions can be performed by Users. ACL modes are defined for each Logistics Object by `memberIdentifier` and include `READ`, `WRITE`, and `CONTROL`. Each mode includes access granted by the modes under it, so `CONTROL` includes `WRITE` and `READ`, and `WRITE` includes `READ`. <strong>Change Request</strong> | A request to modify a Logistics Object, which is processed by the BOL system. Change requests are created when a User wants to update an existing Logistics Object, and they are subject to approval and application by the system. All changes to Logistics Objects are done through change requests. This allows for a controlled and auditable process of modifications. <strong>Host</strong> | A server alias in the BOL system, used to abstract URI values internally. This allows for flexibility in the underlying infrastructure when migrating or changing server configurations. 
 *
 * The version of the OpenAPI document: 7.2.1-develop.2
 * Contact: benoit.tiers@logist-x.eu
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { Rejection } from './Rejection';
import {
    RejectionFromJSON,
    RejectionFromJSONTyped,
    RejectionToJSON,
    RejectionToJSONTyped,
} from './Rejection';
import type { SpecialInstruction } from './SpecialInstruction';
import {
    SpecialInstructionFromJSON,
    SpecialInstructionFromJSONTyped,
    SpecialInstructionToJSON,
    SpecialInstructionToJSONTyped,
} from './SpecialInstruction';
import type { SpecialCharacteristic } from './SpecialCharacteristic';
import {
    SpecialCharacteristicFromJSON,
    SpecialCharacteristicFromJSONTyped,
    SpecialCharacteristicToJSON,
    SpecialCharacteristicToJSONTyped,
} from './SpecialCharacteristic';
import type { Party } from './Party';
import {
    PartyFromJSON,
    PartyFromJSONTyped,
    PartyToJSON,
    PartyToJSONTyped,
} from './Party';
import type { ExternalReference } from './ExternalReference';
import {
    ExternalReferenceFromJSON,
    ExternalReferenceFromJSONTyped,
    ExternalReferenceToJSON,
    ExternalReferenceToJSONTyped,
} from './ExternalReference';
import type { RequestScheduledLocation } from './RequestScheduledLocation';
import {
    RequestScheduledLocationFromJSON,
    RequestScheduledLocationFromJSONTyped,
    RequestScheduledLocationToJSON,
    RequestScheduledLocationToJSONTyped,
} from './RequestScheduledLocation';
import type { QuotationBatchOfGoods } from './QuotationBatchOfGoods';
import {
    QuotationBatchOfGoodsFromJSON,
    QuotationBatchOfGoodsFromJSONTyped,
    QuotationBatchOfGoodsToJSON,
    QuotationBatchOfGoodsToJSONTyped,
} from './QuotationBatchOfGoods';
import type { QuotationRequestServiceOffer } from './QuotationRequestServiceOffer';
import {
    QuotationRequestServiceOfferFromJSON,
    QuotationRequestServiceOfferFromJSONTyped,
    QuotationRequestServiceOfferToJSON,
    QuotationRequestServiceOfferToJSONTyped,
} from './QuotationRequestServiceOffer';
import type { Link } from './Link';
import {
    LinkFromJSON,
    LinkFromJSONTyped,
    LinkToJSON,
    LinkToJSONTyped,
} from './Link';
import type { Contact } from './Contact';
import {
    ContactFromJSON,
    ContactFromJSONTyped,
    ContactToJSON,
    ContactToJSONTyped,
} from './Contact';

/**
 * Response schema for QuotationRequest logistics object.
 * @export
 * @interface QuotationRequestResponse
 */
export interface QuotationRequestResponse {
    /**
     * Date and time the Logistic Object was last submitted.
     * @type {Date}
     * @memberof QuotationRequestResponse
     */
    submissionDateTime?: Date;
    /**
     * The start location where the goods are shipped from.
     * @type {RequestScheduledLocation}
     * @memberof QuotationRequestResponse
     */
    startLocation?: RequestScheduledLocation;
    /**
     * The end location where the goods are delivered.
     * @type {RequestScheduledLocation}
     * @memberof QuotationRequestResponse
     */
    endLocation?: RequestScheduledLocation;
    /**
     * External references of the quotation request can be used when the quotation request is defined in a legacy system that registers the quotation under its own reference identifier. This can be useful for searching the Service using the ID from the legacy app. Frequent ExternalReference Types: PROVIDER_ID.
     * @type {Array<ExternalReference>}
     * @memberof QuotationRequestResponse
     */
    externalReferences?: Array<ExternalReference>;
    /**
     * The requestor refers to the party who initiates the quotation request. In most cases, the requestor is the shipper, but in some cases, such as when dealing with complex organizations, another party may be responsible for requesting a quotation.
     * @type {Party}
     * @memberof QuotationRequestResponse
     */
    requestor?: Party;
    /**
     * Descriptions of goods being handled during the service. For a quotation request, only the description of the goods is required.
     * @type {Array<QuotationBatchOfGoods>}
     * @memberof QuotationRequestResponse
     */
    batchesOfGoods?: Array<QuotationBatchOfGoods>;
    /**
     * Incoterms requested by the customer. The Intercom (International Commercial Terms) is a 3 characters code defining the contractual terms between the seller and the buyer regarding the delivery of the goods and the payment of taxes. The International Chamber of Commerce (ICC) provides the list of these codes.
     * @type {Array<string>}
     * @memberof QuotationRequestResponse
     */
    incoterms?: Array<string>;
    /**
     * Status of the quotation request.
     * List of possible values:
     * - DRAFT: quotation request has been saved before it was submitted to get service offers.
     * - PENDING_OFFERS: pending to get service offers in response to the request .
     * - PENDING_OFFER_SELECTION: Some offers have been proposed. The quotation request is pending for an offer selection to move to the quotation process.
     * - OFFER_SELECTED: an offer was selected. Moving to the quotation process.
     * - MANUAL_QUOTATION_REQUESTED: No offer has been proposed or the offers are not satisfying the requestor who requested a manual quotation
     * - QUOTATION_DONE: at least one quotation was created in response to the quotation request.
     * 
     * @type {string}
     * @memberof QuotationRequestResponse
     */
    quotationRequestStatus?: string;
    /**
     * List of service offers proposed to be used in a quotation process.
     * @type {Array<QuotationRequestServiceOffer>}
     * @memberof QuotationRequestResponse
     */
    serviceOffers?: Array<QuotationRequestServiceOffer>;
    /**
     * 
     * @type {Link}
     * @memberof QuotationRequestResponse
     */
    quotation?: Link;
    /**
     * Preferred payment currency of the request of the service.
     * @type {string}
     * @memberof QuotationRequestResponse
     */
    preferredCurrency?: string;
    /**
     * List of contacts related to the quotation request. This can include persons or teams to be contacted about the service.
     * @type {Array<Contact>}
     * @memberof QuotationRequestResponse
     */
    contacts?: Array<Contact>;
    /**
     * Special instructions applied to the service to take into account when proposing service offers.
     * @type {Array<SpecialInstruction>}
     * @memberof QuotationRequestResponse
     */
    specialInstructions?: Array<SpecialInstruction>;
    /**
     * Special characteristics describing the goods or the service in order to get service offers matching to the requested criteria. For example, if the requested service has to be considered as hazardous, it should appear as a special characteristics.
     * @type {Array<SpecialCharacteristic>}
     * @memberof QuotationRequestResponse
     */
    specialCharacteristics?: Array<SpecialCharacteristic>;
    /**
     * Rejections are reasons explaining why the quotation request is not valid for execution.
     * @type {Array<Rejection>}
     * @memberof QuotationRequestResponse
     */
    rejections?: Array<Rejection>;
    /**
     * Indicates if the quotation is coming from a self care process or a CRM process. Possible values: SELF_CARE or CRM.
     * @type {string}
     * @memberof QuotationRequestResponse
     */
    quotationProcess?: string;
    /**
     * Internal unique identifier of the resource. Id can have different formats like UUID v4, UUID v7, or any other string calculated.
     * @type {string}
     * @memberof QuotationRequestResponse
     */
    id?: string;
}

/**
 * Check if a given object implements the QuotationRequestResponse interface.
 */
export function instanceOfQuotationRequestResponse(value: object): value is QuotationRequestResponse {
    return true;
}

export function QuotationRequestResponseFromJSON(json: any): QuotationRequestResponse {
    return QuotationRequestResponseFromJSONTyped(json, false);
}

export function QuotationRequestResponseFromJSONTyped(json: any, ignoreDiscriminator: boolean): QuotationRequestResponse {
    if (json == null) {
        return json;
    }
    return {
        
        'submissionDateTime': json['submissionDateTime'] == null ? undefined : (new Date(json['submissionDateTime'])),
        'startLocation': json['startLocation'] == null ? undefined : RequestScheduledLocationFromJSON(json['startLocation']),
        'endLocation': json['endLocation'] == null ? undefined : RequestScheduledLocationFromJSON(json['endLocation']),
        'externalReferences': json['externalReferences'] == null ? undefined : ((json['externalReferences'] as Array<any>).map(ExternalReferenceFromJSON)),
        'requestor': json['requestor'] == null ? undefined : PartyFromJSON(json['requestor']),
        'batchesOfGoods': json['batchesOfGoods'] == null ? undefined : ((json['batchesOfGoods'] as Array<any>).map(QuotationBatchOfGoodsFromJSON)),
        'incoterms': json['incoterms'] == null ? undefined : json['incoterms'],
        'quotationRequestStatus': json['quotationRequestStatus'] == null ? undefined : json['quotationRequestStatus'],
        'serviceOffers': json['serviceOffers'] == null ? undefined : ((json['serviceOffers'] as Array<any>).map(QuotationRequestServiceOfferFromJSON)),
        'quotation': json['quotation'] == null ? undefined : LinkFromJSON(json['quotation']),
        'preferredCurrency': json['preferredCurrency'] == null ? undefined : json['preferredCurrency'],
        'contacts': json['contacts'] == null ? undefined : ((json['contacts'] as Array<any>).map(ContactFromJSON)),
        'specialInstructions': json['specialInstructions'] == null ? undefined : ((json['specialInstructions'] as Array<any>).map(SpecialInstructionFromJSON)),
        'specialCharacteristics': json['specialCharacteristics'] == null ? undefined : ((json['specialCharacteristics'] as Array<any>).map(SpecialCharacteristicFromJSON)),
        'rejections': json['rejections'] == null ? undefined : ((json['rejections'] as Array<any>).map(RejectionFromJSON)),
        'quotationProcess': json['quotationProcess'] == null ? undefined : json['quotationProcess'],
        'id': json['id'] == null ? undefined : json['id'],
    };
}

export function QuotationRequestResponseToJSON(json: any): QuotationRequestResponse {
    return QuotationRequestResponseToJSONTyped(json, false);
}

export function QuotationRequestResponseToJSONTyped(value?: QuotationRequestResponse | null, ignoreDiscriminator: boolean = false): any {
    if (value == null) {
        return value;
    }

    return {
        
        'submissionDateTime': value['submissionDateTime'] == null ? value['submissionDateTime'] : value['submissionDateTime'].toISOString(),
        'startLocation': RequestScheduledLocationToJSON(value['startLocation']),
        'endLocation': RequestScheduledLocationToJSON(value['endLocation']),
        'externalReferences': value['externalReferences'] == null ? undefined : ((value['externalReferences'] as Array<any>).map(ExternalReferenceToJSON)),
        'requestor': PartyToJSON(value['requestor']),
        'batchesOfGoods': value['batchesOfGoods'] == null ? undefined : ((value['batchesOfGoods'] as Array<any>).map(QuotationBatchOfGoodsToJSON)),
        'incoterms': value['incoterms'],
        'quotationRequestStatus': value['quotationRequestStatus'],
        'serviceOffers': value['serviceOffers'] == null ? undefined : ((value['serviceOffers'] as Array<any>).map(QuotationRequestServiceOfferToJSON)),
        'quotation': LinkToJSON(value['quotation']),
        'preferredCurrency': value['preferredCurrency'],
        'contacts': value['contacts'] == null ? undefined : ((value['contacts'] as Array<any>).map(ContactToJSON)),
        'specialInstructions': value['specialInstructions'] == null ? undefined : ((value['specialInstructions'] as Array<any>).map(SpecialInstructionToJSON)),
        'specialCharacteristics': value['specialCharacteristics'] == null ? undefined : ((value['specialCharacteristics'] as Array<any>).map(SpecialCharacteristicToJSON)),
        'rejections': value['rejections'] == null ? undefined : ((value['rejections'] as Array<any>).map(RejectionToJSON)),
        'quotationProcess': value['quotationProcess'],
        'id': value['id'],
    };
}

